import { useMemo } from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { ImageData } from "../../../types";
import type { Match } from "../types";
import { compareEventTimestamps } from "../../../lib/eventTime";
import { isShootoutKick } from "../utils/penaltyShootout";
import { sameTeamId } from "../utils/teamId";

/** Per-player aggregate stats derived from live + queued events. */
export interface PlayerStats {
  playerId: string;
  playerName: string;
  jerseyNumber: number;
  teamSide: "home" | "away";
  /** Total events where this player is the author */
  totalEvents: number;
  passesGood: number;
  passesBad: number;
  passesReceived: number;
  shots: number;
  shotsOnTarget: number;
  /** PMA-COCK-167: Shot/Header author, outcome ∈ {OffTarget, Post}. */
  shotsOffTarget: number;
  /** PMA-COCK-165/167: Shot/Header author, outcome === Blocked (credited
   * regardless of whether a blocker was picked). */
  shotsBlocked: number;
  goals: number;
  /** EPIC-94 (PMA-COCK-037 addendum): own goals conceded by this player.
   * Never counted in `goals`, `shots` or `shotsOnTarget` — see the `Shot`/
   * `Header` case's `data.own_goal` guard below. The score still credits
   * the benefiting team via `useLiveScore` (untouched by this UC).
   * Optional (like `photoUrl`/`photoImage` below) rather than required —
   * this hook always sets a real number in `getOrCreate`, but keeping the
   * field optional on the TYPE avoids rippling a required-field break
   * into every hand-built `PlayerStats` fixture across the codebase for a
   * field most consumers don't need to construct themselves. */
  ownGoals?: number;
  penalties: number;
  penaltyGoals: number;
  /** EPIC-98 (item 4/AC4): one per non-shootout `SetPiece` with
   * `set_piece_type`/`action` `Penalty` and outcome `Complete`, credited to
   * the event's `player_id` — the fouled player who WON the penalty, not
   * the taker. This is the PMA-COCK-038 foul-award event
   * (`useActionFlow.ts`'s "Penal cometido" branch) that `penalties`/
   * `penaltyGoals` deliberately exclude (see the SetPiece case below) to
   * avoid double-counting against the separate penalty-kick Shot
   * (PMA-COCK-175). Never counted in `penalties`, `penaltyGoals` or
   * `foulsReceived` — those three stay exactly as they were. */
  penaltiesWon: number;
  /** Shootout kicks taken. Never counted in `penalties` — see the SetPiece case. */
  shootoutKicks: number;
  /** Shootout kicks scored. Never counted in `goals`. */
  shootoutGoals: number;
  assists: number;
  duelsWon: number;
  duelsLost: number;
  foulsCommitted: number;
  foulsReceived: number;
  interceptions: number;
  recoveries: number;
  clearances: number;
  /** PMA-COCK-165: generic Block-event author credit (unchanged) PLUS the
   * player named in `data.blocked_by_player_id` on a Blocked Shot/Header. */
  blocks: number;
  /** PMA-COCK-177: the goalkeeper named in `data.saved_by_player_id` on a
   * Saved Shot/Header — DirectShot's on-target-only destination tap
   * (PMA-COCK-176) or the generic More-actions Shot flow (PMA-COCK-172).
   * Never credited from a shootout kick (a Saved shootout kick is a
   * SetPiece event, so this Shot/Header case never even sees one — the
   * `isShootoutKick` guard is defensive symmetry with `blocks`/`penalties`
   * above, not load-bearing). */
  saves: number;
  yellowCards: number;
  redCards: number;
  /** PMA-COCK-167 "Toques": authored events not in TOUCH_EXCLUDED_TYPES and
   * not a shootout kick. See that constant's doc comment for the exact set. */
  touches: number;
  photoUrl?: string | null;
  photoImage?: ImageData | null;
}

const EMPTY: PlayerStats[] = [];

/**
 * PMA-COCK-167 "Toques" exclusion list — client decision 2026-09-07: a
 * touch is any authored event EXCEPT these, and except a shootout kick
 * (checked separately via `isShootoutKick`). Verified against
 * `BE/app/models/event.py`'s `EventType` enum:
 *   - Card, Substitution, FoulCommitted — explicit client exclusions.
 *   - VARDecision — "VAR" in the client's list; not currently emitted by
 *     the cockpit (no `type: "VARDecision"` sender exists), kept for safety.
 *   - GameStoppage — covers every "period/clock control event" (kickoff/
 *     halftime/VAR-timer/timeout stoppages) AND "Injury" in the client's
 *     list: Injury has no dedicated FE EventType, it's a GameStoppage
 *     stoppage reason (`useIneffectiveTime.ts`). GameStoppage events also
 *     carry no top-level `player_id` today, so this entry is defense in
 *     depth rather than load-bearing — Substitution IS load-bearing (it
 *     carries `player_id: playerOffId`, `useCockpitSubstitutionFlow.ts`).
 */
export const TOUCH_EXCLUDED_EVENT_TYPES: ReadonlySet<string> = new Set([
  "Card",
  "Substitution",
  "FoulCommitted",
  "VARDecision",
  "GameStoppage",
]);

/**
 * Aggregates per-player statistics from match events.
 *
 * Author player is always `event.player_id`.
 * Destination / target player varies by event type and lives in `event.data`:
 *   - Pass → `data.receiver_id`
 *   - FoulCommitted → `data.target_player_id`
 *   - Duel → `data.target_player_id`
 */
export function usePlayerStats(
  match: Match | null,
  events: MatchEvent[],
): PlayerStats[] {
  return useMemo(() => {
    if (!match || events.length === 0) return EMPTY;

    const homeTeamId = match.home_team.id;

    // Build a lookup: playerId → { name, jersey, teamSide }
    const playerLookup = new Map<
      string,
      {
        name: string;
        jersey: number;
        teamSide: "home" | "away";
        photoUrl?: string | null;
        photoImage?: ImageData | null;
      }
    >();
    for (const p of match.home_team.players) {
      playerLookup.set(p.id, {
        name: p.short_name || p.full_name,
        jersey: p.jersey_number,
        teamSide: "home",
        photoUrl: p.photo_url ?? null,
        photoImage: p.photo_image ?? null,
      });
    }
    for (const p of match.away_team.players) {
      playerLookup.set(p.id, {
        name: p.short_name || p.full_name,
        jersey: p.jersey_number,
        teamSide: "away",
        photoUrl: p.photo_url ?? null,
        photoImage: p.photo_image ?? null,
      });
    }

    const statsMap = new Map<string, PlayerStats>();

    const getOrCreate = (playerId: string): PlayerStats => {
      let s = statsMap.get(playerId);
      if (!s) {
        const info = playerLookup.get(playerId);
        s = {
          playerId,
          playerName: info?.name ?? `#${playerId}`,
          jerseyNumber: info?.jersey ?? 0,
          teamSide: info?.teamSide ?? "home",
          photoUrl: info?.photoUrl ?? null,
          photoImage: info?.photoImage ?? null,
          totalEvents: 0,
          passesGood: 0,
          passesBad: 0,
          passesReceived: 0,
          shots: 0,
          shotsOnTarget: 0,
          shotsOffTarget: 0,
          shotsBlocked: 0,
          goals: 0,
          ownGoals: 0,
          penalties: 0,
          penaltyGoals: 0,
          penaltiesWon: 0,
          shootoutKicks: 0,
          shootoutGoals: 0,
          assists: 0,
          duelsWon: 0,
          duelsLost: 0,
          foulsCommitted: 0,
          foulsReceived: 0,
          interceptions: 0,
          recoveries: 0,
          clearances: 0,
          blocks: 0,
          saves: 0,
          yellowCards: 0,
          redCards: 0,
          touches: 0,
        };
        statsMap.set(playerId, s);
      }
      return s;
    };

    for (const event of events) {
      const authorId = event.player_id;
      if (!authorId) continue;

      const isHomeTeam = sameTeamId(event.team_id, homeTeamId);
      const author = getOrCreate(authorId);

      // Ensure teamSide is correct based on event data
      if (!playerLookup.has(authorId)) {
        author.teamSide = isHomeTeam ? "home" : "away";
      }

      author.totalEvents++;

      const data = event.data ?? {};
      const type = event.type;

      if (!TOUCH_EXCLUDED_EVENT_TYPES.has(type) && !isShootoutKick(event)) {
        author.touches++;
      }

      switch (type) {
        case "Pass": {
          const outcome = String(data.outcome ?? "").toLowerCase();
          if (outcome === "complete") {
            author.passesGood++;
            // Credit the receiver with passesReceived
            const receiverId =
              data.receiver_id ?? data.recipient_id ?? undefined;
            if (receiverId) {
              getOrCreate(receiverId).passesReceived++;
            }
          } else {
            // Incomplete, Out, Pass Offside
            author.passesBad++;
          }
          break;
        }

        case "Shot":
        case "Header": {
          // EPIC-94 (PMA-COCK-037 addendum): an own goal is dispatched with
          // `team_id` = the benefiting team and `player_id` = the
          // conceding player (locked decision, `useActionFlow.ts`'s
          // `handleQuickActionSelect("OwnGoal")`). It must count only as
          // an own goal for the conceding player — never as a shot, a
          // shot on target, or a goal. The score (`useLiveScore`) and the
          // "(AG)" label already key off `data.own_goal`/`team_id`
          // independently of this hook and are unaffected.
          if (data.own_goal) {
            author.ownGoals = (author.ownGoals ?? 0) + 1;
            break;
          }
          author.shots++;
          const outcome = String(data.outcome ?? "").toLowerCase();
          if (
            outcome === "goal" ||
            outcome === "saved" ||
            outcome === "ontarget" ||
            outcome === "on target"
          ) {
            author.shotsOnTarget++;
          } else if (
            outcome === "offtarget" ||
            outcome === "off target" ||
            outcome === "post"
          ) {
            author.shotsOffTarget++;
          } else if (outcome === "blocked") {
            author.shotsBlocked++;
            // PMA-COCK-165: only set when the blocker step (BlockedShot
            // quick action) actually resolved to a real player — a manual
            // Shot/Header logged with outcome Blocked via the generic flow
            // has no blocked_by_player_id and credits nobody.
            const blockerId = data.blocked_by_player_id;
            if (blockerId) {
              getOrCreate(blockerId).blocks++;
            }
          }
          if (outcome === "goal") {
            author.goals++;
          }
          // PMA-COCK-177 (goalkeeper saves become a counted stat, user
          // decision 2026-09-09): the keeper named in
          // `data.saved_by_player_id` on a Saved Shot/Header gets a
          // counted save. Never from a shootout kick — defensive symmetry
          // with the `blocks`/`penalties` guards above; a Saved shootout
          // kick is dispatched as a SetPiece event, so this Shot/Header
          // case never actually sees one.
          if (outcome === "saved" && !isShootoutKick(event)) {
            const keeperId = data.saved_by_player_id;
            if (keeperId) {
              getOrCreate(keeperId).saves++;
            }
          }
          // PMA-COCK-175 ("Tiro Penal"): a penalty kick logged via the
          // quick action dispatches as a plain Shot (shot_type: "Penalty",
          // see useActionFlow.ts's handleOutcomeClick) — shots/goals above
          // already cover it identically to any other shot. `penalties`/
          // `penaltyGoals` are the ones that need a dedicated add here,
          // same shootout guard as the SetPiece case below (never actually
          // true for a Shot-type event — is_shootout is only ever set on a
          // shootout SetPiece — kept for defensive symmetry/documentation).
          if (data.shot_type === "Penalty" && !isShootoutKick(event)) {
            author.penalties++;
            if (outcome === "goal") {
              author.penaltyGoals++;
            }
          }
          break;
        }

        case "SetPiece": {
          const action = String(
            data.set_piece_type ?? data.action ?? "",
          ).toLowerCase();
          const outcome = String(data.outcome ?? "").toLowerCase();
          if (action === "penalty") {
            // A shootout kick is its own statistic. It is not a penalty won
            // during play, and a shootout goal is not a goal — the result
            // stands as it was when normal time ended, and every stats
            // convention keeps the two apart. `is_shootout` is the flag that
            // separates them; see utils/penaltyShootout.ts.
            if (isShootoutKick(event)) {
              author.shootoutKicks++;
              if (outcome === "goal") author.shootoutGoals++;
            } else if (
              outcome === "goal" ||
              outcome === "saved" ||
              outcome === "missed"
            ) {
              // PMA-COCK-175: `penalties`/`penaltyGoals` now come primarily
              // from the penalty-kick Shot above. PMA-COCK-038's foul-award
              // SetPiece/Penalty event (FoulCommitted's opponent-side
              // counterpart) is dispatched with `outcome: "Complete"` —
              // always truthy, never "no outcome" — so gating on outcome
              // PRESENCE alone would not dedupe it. Gating on a REAL kick
              // result (Goal/Saved/Missed, i.e. SetPiece's own
              // ACTION_FLOWS.SetPiece.Penalty outcome set) keeps this
              // branch counting only when an operator manually logs the
              // kick's SetPiece/Penalty outcome via the generic "More
              // actions" panel (a still-valid, pre-existing path,
              // unchanged — see usePlayerStats.test.ts's
              // inPlayPenalty/shootoutKick fixtures, none of which use
              // "Complete") — the award event itself no longer
              // double-counts against the new Shot-based count.
              author.penalties++;
              if (outcome === "goal") {
                author.penaltyGoals++;
                author.goals++;
              }
            } else if (outcome === "complete") {
              // EPIC-98 (item 4/AC4): the PMA-COCK-038 foul-award event
              // itself — dispatched by "Penal cometido" with `outcome:
              // "Complete"`, credited to the fouled player (the one who WON
              // the penalty, not the taker). Deliberately its own branch,
              // never touching `penalties`/`penaltyGoals` above (those stay
              // scoped to a real kick result, per PMA-COCK-175).
              author.penaltiesWon++;
            }
          }
          break;
        }

        case "Duel": {
          const outcome = String(data.outcome ?? "").toLowerCase();
          if (outcome === "won") {
            author.duelsWon++;
          } else {
            author.duelsLost++;
          }
          break;
        }

        case "FoulCommitted": {
          author.foulsCommitted++;
          // Credit the fouled player with foulsReceived
          const fouledId =
            data.fouled_player_id ?? data.target_player_id ?? data.opponent_id;
          if (fouledId) {
            getOrCreate(fouledId).foulsReceived++;
          }
          break;
        }

        case "Card": {
          const cardType = String(data.card_type ?? "").toLowerCase();
          if (cardType.includes("cancel")) {
            // Cancelled cards are not counted
          } else if (cardType.includes("red")) {
            author.redCards++;
          } else if (cardType.includes("yellow")) {
            author.yellowCards++;
          }
          break;
        }

        case "Interception":
          author.interceptions++;
          break;

        case "Recovery":
          author.recoveries++;
          break;

        case "Clearance":
          author.clearances++;
          break;

        case "Block":
          author.blocks++;
          break;

        default:
          break;
      }
    }

    // ── Assist detection ──────────────────────────────────────────────
    // Sort events chronologically and for each goal, find the most recent
    // complete pass from the same team whose receiver is the goal scorer.
    const sorted = [...events].sort((a, b) => {
      const clockA = String(a.match_clock ?? "");
      const clockB = String(b.match_clock ?? "");
      if (clockA !== clockB) return clockA < clockB ? -1 : 1;
      // Mixed optimistic ("...Z") and server-acked (naive) timestamps must
      // be read with one interpretation or assist detection pairs a goal with
      // the wrong pass — see `lib/eventTime.ts`.
      return compareEventTimestamps(a.timestamp, b.timestamp);
    });

    for (let i = 0; i < sorted.length; i++) {
      const ev = sorted[i];
      const data = ev.data ?? {};
      const outcome = String(data.outcome ?? "").toLowerCase();
      const isGoal =
        (ev.type === "Shot" && outcome === "goal" && !data.own_goal) ||
        (ev.type === "Header" && outcome === "goal") ||
        (ev.type === "SetPiece" &&
          String(data.set_piece_type ?? "").toLowerCase() === "penalty" &&
          outcome === "goal" &&
          // A shootout kick has no build-up play, so it can never carry an
          // assist. Without this guard the walk-back would credit whoever
          // last completed a pass in extra time.
          !isShootoutKick(ev));

      if (!isGoal || !ev.player_id) continue;

      // Walk backwards to find the last complete pass to the scorer
      for (let j = i - 1; j >= 0; j--) {
        const prev = sorted[j];
        if (prev.type !== "Pass") continue;
        if (prev.team_id !== ev.team_id) continue;
        const prevData = prev.data ?? {};
        const prevOutcome = String(prevData.outcome ?? "").toLowerCase();
        if (prevOutcome !== "complete") continue;
        const receiverId = prevData.receiver_id ?? prevData.recipient_id;
        if (receiverId === ev.player_id && prev.player_id) {
          getOrCreate(prev.player_id).assists++;
        }
        break; // Only the most recent pass counts
      }
    }

    // Return sorted: home first, then away, within each group by jersey number
    return Array.from(statsMap.values()).sort((a, b) => {
      if (a.teamSide !== b.teamSide) {
        return a.teamSide === "home" ? -1 : 1;
      }
      return a.jerseyNumber - b.jerseyNumber;
    });
  }, [match, events]);
}
